/**
 * Debug Logs — admin-facing aggregate view of device-side log batches
 * collected from the in-app "logtest" debug mode (v1.2.4+).
 *
 * Two views in one page:
 *   • Report — high-level stats (totals, by-version, by-device-fleet, top
 *              errors) so the dev team can spot patterns at a glance.
 *   • Sessions — most-recent log batches, expandable to see entries +
 *              the device-info snapshot that came with them.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { adminAxios } from "../lib/api";
import { FileText, RefreshCw, Trash2, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

function levelColor(level) {
  return level === "error"
    ? "text-red-700"
    : level === "warn"
    ? "text-amber-700"
    : level === "info"
    ? "text-sky-700"
    : "text-slate-600";
}

function Section({ title, children }) {
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
          {title}
        </h3>
        {children}
      </CardContent>
    </Card>
  );
}

export default function DebugLogs() {
  const { toast } = useToast();
  const [tab, setTab] = useState("report"); // "report" | "sessions"
  const [report, setReport] = useState(null);
  const [sessions, setSessions] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filterDevice, setFilterDevice] = useState("");
  const [filterErrors, setFilterErrors] = useState(false);

  const loadReport = async () => {
    const r = await adminAxios.get("/debug-logs/report").then((x) => x.data);
    setReport(r);
  };

  const loadSessions = async () => {
    const params = {};
    if (filterDevice) params.device_id = filterDevice;
    if (filterErrors) params.level = "error";
    const r = await adminAxios.get("/debug-logs", { params }).then((x) => x.data);
    setSessions(r);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await Promise.all([loadReport(), loadSessions()]);
    } catch (e) {
      toast({
        title: "Failed to load logs",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Re-load sessions when filters change (cheap call).
  useEffect(() => {
    loadSessions().catch(() => {});
  }, [filterDevice, filterErrors]);

  const handleClearAll = async () => {
    if (!window.confirm("Delete ALL captured debug log sessions? Cannot be undone."))
      return;
    try {
      await adminAxios.post("/debug-logs/clear");
      toast({ title: "All log sessions cleared" });
      refresh();
    } catch (e) {
      toast({
        title: "Clear failed",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteOne = async (id) => {
    try {
      await adminAxios.delete(`/debug-logs/${id}`);
      refresh();
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  const summary = report?.summary || { total_sessions: 0, total_errors: 0, unique_devices: 0 };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-orange-500" />
            Debug Logs
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Captured from devices that activated with the magic{" "}
            <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">logtest</code>{" "}
            key — gives you crash traces, parse failures and device-fleet
            stats without asking users for adb logcat.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refresh} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={handleClearAll}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear all
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Section title="Sessions captured">
          <div className="text-3xl font-bold text-slate-900">
            {summary.total_sessions.toLocaleString()}
          </div>
        </Section>
        <Section title="Total errors">
          <div className="text-3xl font-bold text-red-600">
            {summary.total_errors.toLocaleString()}
          </div>
        </Section>
        <Section title="Unique devices">
          <div className="text-3xl font-bold text-sky-700">
            {summary.unique_devices.toLocaleString()}
          </div>
        </Section>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setTab("report")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "report"
              ? "border-orange-500 text-orange-700"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Report
        </button>
        <button
          onClick={() => setTab("sessions")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "sessions"
              ? "border-orange-500 text-orange-700"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Sessions ({sessions.total})
        </button>
      </div>

      {tab === "report" && report && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Section title="Top errors (last 14 days)">
            {report.top_errors.length === 0 ? (
              <p className="text-sm text-slate-500">No errors captured yet 🎉</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Message</TableHead>
                    <TableHead className="text-right w-16">Count</TableHead>
                    <TableHead className="text-right w-20">Devices</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.top_errors.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-[11px] text-red-700 max-w-md break-words">
                        {e.snippet}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{e.count}</TableCell>
                      <TableCell className="text-right tabular-nums">{e.unique_devices}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>

          <Section title="By app version">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Devices</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.by_version.map((v) => (
                  <TableRow key={v.version || "(unknown)"}>
                    <TableCell className="font-mono">{v.version || "(unknown)"}</TableCell>
                    <TableCell className="text-right tabular-nums">{v.sessions}</TableCell>
                    <TableCell className={`text-right tabular-nums ${v.errors > 0 ? "text-red-600 font-semibold" : ""}`}>
                      {v.errors}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{v.unique_devices}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>

          <Section title="Device fleet">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Android</TableHead>
                  <TableHead>Form factor</TableHead>
                  <TableHead className="text-right">Sessions</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.by_platform.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell>{p.manufacturer || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{p.model || "—"}</TableCell>
                    <TableCell>{p.android_version || "—"}</TableCell>
                    <TableCell>{p.form_factor || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.sessions}</TableCell>
                    <TableCell className={`text-right tabular-nums ${p.errors > 0 ? "text-red-600 font-semibold" : ""}`}>
                      {p.errors}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </div>
      )}

      {tab === "sessions" && (
        <div className="space-y-3">
          <div className="flex gap-2 items-center">
            <Input
              placeholder="Filter by device id…"
              value={filterDevice}
              onChange={(e) => setFilterDevice(e.target.value)}
              className="max-w-xs"
            />
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={filterErrors}
                onChange={(e) => setFilterErrors(e.target.checked)}
              />
              Only sessions with errors
            </label>
          </div>

          {sessions.items.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center text-slate-500">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No log sessions yet. Sideload v1.2.4+ on a test device and activate with <code>logtest</code> to start collecting.</p>
              </CardContent>
            </Card>
          ) : (
            sessions.items.map((s) => {
              const isOpen = expanded === s.id;
              const dev = s.device_info || {};
              return (
                <Card key={s.id}>
                  <CardContent className="p-0">
                    <button
                      onClick={() => setExpanded(isOpen ? null : s.id)}
                      className="w-full p-4 flex items-start gap-3 text-left hover:bg-slate-50"
                    >
                      <div className="pt-1">
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-slate-700">
                            {dev.manufacturer || "unknown"} {dev.model || ""}
                          </span>
                          {dev.form_factor && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                              {dev.form_factor}
                            </span>
                          )}
                          {s.app_version && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">
                              v{s.app_version}
                            </span>
                          )}
                          {dev.android_version && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                              Android {dev.android_version}
                            </span>
                          )}
                          {s.error_count > 0 && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 inline-flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {s.error_count} errors
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1 font-mono truncate">
                          device {s.device_id} · {new Date(s.created_at).toLocaleString()} · {s.entries.length} entries
                        </div>
                      </div>
                      <button
                        onClick={(ev) => {
                          ev.stopPropagation();
                          handleDeleteOne(s.id);
                        }}
                        className="text-slate-400 hover:text-red-600"
                        title="Delete this session"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </button>
                    {isOpen && (
                      <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 space-y-3">
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                            Device info
                          </h4>
                          <pre className="text-[11px] font-mono bg-white border border-slate-200 rounded p-2 overflow-x-auto">
                            {JSON.stringify(dev, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                            Log entries
                          </h4>
                          <div className="bg-white border border-slate-200 rounded max-h-96 overflow-y-auto divide-y divide-slate-100 font-mono text-[11px]">
                            {s.entries.map((e, i) => (
                              <div key={i} className={`px-2 py-1 ${levelColor(e.level)}`}>
                                <span className="text-slate-400 mr-2">
                                  {new Date(e.ts).toISOString().slice(11, 19)}
                                </span>
                                <span className="uppercase text-[9px] mr-2 text-slate-500">
                                  {e.level}
                                </span>
                                <span className="whitespace-pre-wrap break-words">{e.msg}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
